import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Spinner,
  Alert,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

const TEMPLATE_FIELDS = [
  { field: 'Title', label: 'Title (In-App & Push)', rows: 1 },
  { field: 'InAppTemplate', label: 'In-App Template', rows: 3 },
  { field: 'PushNotificationTemplate', label: 'Push Notification Template', rows: 3 },
  { field: 'EmailSubjectTemplate', label: 'Email Subject Template', rows: 2 },
  { field: 'EmailBodyTemplate', label: 'Email Body Template', rows: 8 },
];

const emptyForm = () => ({
  Title: '',
  InAppTemplate: '',
  PushNotificationTemplate: '',
  EmailSubjectTemplate: '',
  EmailBodyTemplate: '',
});

export const AdminNotificationTypes = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [testingType, setTestingType] = useState(null);
  const [testConfirmOpen, setTestConfirmOpen] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testError, setTestError] = useState(null);
  const [testSuccess, setTestSuccess] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getNotificationTypeTemplates();
      setTemplates(data || []);
    } catch (err) {
      console.error('Failed to load notification type templates:', err);
      setError(err.message || 'Failed to load notification type templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openEdit = (template) => {
    setEditingTemplate(template);
    setForm({
      Title: template.Title || '',
      InAppTemplate: template.InAppTemplate || '',
      PushNotificationTemplate: template.PushNotificationTemplate || '',
      EmailSubjectTemplate: template.EmailSubjectTemplate || '',
      EmailBodyTemplate: template.EmailBodyTemplate || '',
    });
    setSaveError(null);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditingTemplate(null);
    setSaveError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setSaveError(null);
      await apiClient.updateNotificationTypeTemplate(editingTemplate.NotificationType, form);
      setTemplates(prev =>
        prev.map(t =>
          t.NotificationType === editingTemplate.NotificationType
            ? { ...t, ...form }
            : t
        )
      );
      setEditingTemplate(null);
    } catch (err) {
      console.error('Failed to save template:', err);
      setSaveError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const openTestConfirm = (template) => {
    setTestingType(template);
    setTestError(null);
    setTestSuccess(null);
    setTestConfirmOpen(true);
  };

  const closeTestConfirm = () => {
    if (testSending) return;
    setTestConfirmOpen(false);
    setTestingType(null);
  };

  const handleTest = async () => {
    try {
      setTestSending(true);
      setTestError(null);
      await apiClient.testNotificationType(testingType.NotificationType);
      setTestSuccess(`Test notifications for "${testingType.NotificationType}" have been queued successfully.`);
      setTestConfirmOpen(false);
      setTestingType(null);
    } catch (err) {
      console.error('Failed to send test notification:', err);
      setTestError(err.message || 'Failed to send test notification');
    } finally {
      setTestSending(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Notification Types</h2>
      </div>

      {error && <Alert color="danger" fade={false}>{error}</Alert>}
      {testSuccess && <Alert color="success" fade={false} toggle={() => setTestSuccess(null)}>{testSuccess}</Alert>}

      {loading ? (
        <div className="text-center p-5">
          <Spinner color="primary" />
          <p className="mt-2">Loading...</p>
        </div>
      ) : (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Notification Type</th>
              <th>Title</th>
              <th>In-App Template</th>
              <th>Push Template</th>
              <th>Email Subject</th>
              <th style={{ width: '160px' }}></th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-muted">No notification types found</td>
              </tr>
            ) : (
              templates.map((t) => (
                <tr key={t.NotificationType}>
                  <td><strong>{t.NotificationType}</strong></td>
                  <td className="text-truncate" style={{ maxWidth: '160px' }}>{t.Title}</td>
                  <td className="text-truncate" style={{ maxWidth: '200px' }}>{t.InAppTemplate}</td>
                  <td className="text-truncate" style={{ maxWidth: '200px' }}>{t.PushNotificationTemplate}</td>
                  <td className="text-truncate" style={{ maxWidth: '200px' }}>{t.EmailSubjectTemplate}</td>
                  <td>
                    <Button color="primary" size="sm" onClick={() => openEdit(t)} className="mr-1">
                      Edit
                    </Button>
                    &nbsp;
                    <Button color="warning" size="sm" onClick={() => openTestConfirm(t)}>
                      Test
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      <Modal isOpen={!!editingTemplate} toggle={closeEdit} size="lg" centered>
        <ModalHeader toggle={closeEdit}>
          Edit Templates — <em>{editingTemplate?.NotificationType}</em>
        </ModalHeader>
        <Form onSubmit={handleSave}>
          <ModalBody>
            {saveError && <Alert color="danger">{saveError}</Alert>}
            <p className="text-muted small mb-3">
              Available placeholders: <code>[User.Name]</code>, <code>[Model.Name]</code>,{' '}
              <code>[Model.Id]</code>, <code>[Signal.Time]</code>, <code>[Signal.Action]</code>
            </p>
            {TEMPLATE_FIELDS.map(({ field, label, rows }) => (
              <FormGroup key={field}>
                <Label for={field}>{label}</Label>
                <Input
                  type="textarea"
                  id={field}
                  rows={rows}
                  value={form[field]}
                  onChange={(e) => setForm(prev => ({ ...prev, [field]: e.target.value }))}
                  disabled={saving}
                  style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                />
              </FormGroup>
            ))}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeEdit} disabled={saving}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" /> : 'Save'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={testConfirmOpen} toggle={closeTestConfirm} centered>
        <ModalHeader toggle={closeTestConfirm}>
          Test Notification — <em>{testingType?.NotificationType}</em>
        </ModalHeader>
        <ModalBody>
          {testError && <Alert color="danger">{testError}</Alert>}
          <Alert color="warning" fade={false}>
            <strong>Warning:</strong> This will immediately send real notifications to{' '}
            <strong>all users</strong> who have subscribed to the{' '}
            <strong>{testingType?.NotificationType}</strong> notification type.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeTestConfirm} disabled={testSending}>
            Cancel
          </Button>
          <Button color="warning" onClick={handleTest} disabled={testSending}>
            {testSending ? <Spinner size="sm" /> : 'Send Test Notifications'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
